import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { useToast } from 'core/components/toast';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import { NotificationCard } from 'core/components/notification-card';
import darkInfoIcon from 'core/assets/svg/ic-info-black.svg';

import OfferDocumentUpload from './offer-document-upload';
import OfferDocumentList from './offer-document-list';
import useStyles from './offer-document-modal.styles';

const OfferDocumentModal = ({ open, onClose }) => {
  const classes = useStyles();
  const toast = useToast();
  const { viewOnly } = useSession();
  const {
    offer: { documents, documentsLoading, webUserId, uploadDocument, downloadDocument, fetchDocuments }
  } = useStore();

  // Fetch documents when modal opens
  useEffect(() => {
    if (open && webUserId) {
      fetchDocuments().catch((error) => {
        console.error(error);
        toast.notify({
          type: 'error',
          message: 'Failed to load documents'
        });
      });
    }
  }, [open, webUserId, fetchDocuments, toast]);

  const handleUpload = async (file, onProgress) => {
    try {
      const uploadedDocument = await uploadDocument(file, onProgress);
      toast.notify({
        type: 'success',
        message: 'Document uploaded successfully'
      });
      return uploadedDocument;
    } catch (error) {
      toast.notify({
        type: 'error',
        message: 'Failed to upload document'
      });
      throw error;
    }
  };

  const handleDownload = async (path) => {
    try {
      await downloadDocument(path);
    } catch (error) {
      toast.notify({
        type: 'error',
        message: 'Failed to download document'
      });
      throw error;
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="md" classes={{ paper: classes.container }}>
      <Label type="greenBig" className={classes.modalHeader}>
        Supporting Documents
      </Label>
      <Label type="darkGreenSmall" className={classes.modalContent}>
        Upload any requested documents here to confirm applicants or insured items meet conditions related to discounts,
        rejections, or previous coverage.
      </Label>

      <NotificationCard iconSize="icon16" icon={darkInfoIcon} type="light">
        <span>
          Got any task documents? You can upload them in the <b>Post-purchase tasks</b> section at Checkout.
        </span>
      </NotificationCard>

      <div className={classes.content}>
        <Card className={classes.documentsCard}>
          <OfferDocumentList documents={documents} onDownload={handleDownload} loading={documentsLoading} />
          {!viewOnly && <OfferDocumentUpload onUpload={handleUpload} />}
        </Card>
      </div>
    </BaseDialog>
  );
};

OfferDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default observer(OfferDocumentModal);
